/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, alpha, Autocomplete, TextField, InputAdornment,
    Chip, Collapse,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { common } from 'src/theme/palette';

// ── Treatment pill ─────────────────────────────────────────────────────────
const TreatmentPill = ({ name, isSelected, isExtra, onToggle }) => (
    <Box
        onClick={onToggle}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: '12px',
            py: '6px',
            borderRadius: '20px',
            border: '1.5px solid',
            borderColor: isSelected ? common.mintyZest : 'divider',
            bgcolor: isSelected ? alpha(common.mintyZest, 0.08) : '#fff',
            color: isSelected ? common.mintyZest : 'text.secondary',
            fontSize: '12px',
            fontWeight: isSelected ? 700 : 500,
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
            '&:hover': {
                borderColor: common.mintyZest,
                bgcolor: alpha(common.mintyZest, 0.06),
                transform: 'translateY(-1px)',
                boxShadow: `0 2px 8px ${alpha(common.mintyZest, 0.15)}`,
            },
        }}
    >
        {isSelected && (
            <Iconify icon="eva:checkmark-fill" width={14} sx={{ mr: '-2px' }} />
        )}
        {name}
        {isExtra && (
            <Chip
                label="added"
                size="small"
                sx={{
                    ml: 0.5,
                    height: 16,
                    fontSize: '9px',
                    fontWeight: 700,
                    bgcolor: alpha('#078DEE', 0.1),
                    color: '#078DEE',
                    '& .MuiChip-label': { px: 0.75 },
                }}
            />
        )}
    </Box>
);

TreatmentPill.propTypes = {
    name: PropTypes.string.isRequired,
    isSelected: PropTypes.bool,
    isExtra: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
};

// ── Main component ─────────────────────────────────────────────────────────

const TreatmentSelector = ({
    treatments,
    selectedTreatmentIds,
    onToggle,
    practiceTreatments,
    extraTreatmentIds,
    onAddExtra,
    onRemoveExtra,
}) => {
    const [addingMore, setAddingMore] = useState(false);

    // Practice treatments not already in the lead's list
    const availableExtras = useMemo(() => {
        if (!practiceTreatments?.length) return [];
        const leadTreatmentNames = new Set(
            (treatments || []).map((t) =>
                (t.treatment?.name || t.treatment_name || '').toLowerCase()
            )
        );
        return practiceTreatments.filter(
            (pt) => !leadTreatmentNames.has((pt.name || '').toLowerCase())
        );
    }, [practiceTreatments, treatments]);

    const hasExtras = extraTreatmentIds && extraTreatmentIds.length > 0;
    const showAddButton = onAddExtra && availableExtras.length > 0;

    return (
        <Box>
            {/* Helper info */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    bgcolor: alpha('#078DEE', 0.04),
                    border: '1px solid',
                    borderColor: alpha('#078DEE', 0.1),
                }}
            >
                <Iconify icon="eva:info-outline" width={16} sx={{ color: '#078DEE', mt: '1px', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#637381', lineHeight: 1.55, fontSize: '11.5px' }}>
                    Forms will automatically use the patient&apos;s treatment context. Select which treatments to include, or add extras from your practice list.
                </Typography>
            </Box>

            {/* Lead treatments */}
            {(!treatments || treatments.length === 0) ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No active treatments found for this patient.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                    {treatments.map((t) => {
                        const treatmentId = t.id;
                        const name = t.treatment?.name || t.treatment_name || `Treatment ${treatmentId}`;
                        const isSelected = selectedTreatmentIds.includes(treatmentId);
                        return (
                            <TreatmentPill
                                key={treatmentId}
                                name={name}
                                isSelected={isSelected}
                                onToggle={() => onToggle(treatmentId)}
                            />
                        );
                    })}

                    {/* Extra (practice-level) treatments */}
                    {hasExtras && extraTreatmentIds.map((ptId) => {
                        const pt = (practiceTreatments || []).find((p) => p.id === ptId);
                        if (!pt) return null;
                        return (
                            <TreatmentPill
                                key={`extra-${ptId}`}
                                name={pt.name}
                                isSelected
                                isExtra
                                onToggle={() => onRemoveExtra(ptId)}
                            />
                        );
                    })}
                </Box>
            )}

            {/* Add more button + dropdown */}
            {showAddButton && (
                <Box sx={{ mt: 0.5 }}>
                    {!addingMore ? (
                        <Box
                            onClick={() => setAddingMore(true)}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#637381',
                                transition: 'all 0.15s',
                                '&:hover': {
                                    bgcolor: alpha(common.mintyZest, 0.06),
                                    color: common.mintyZest,
                                },
                            }}
                        >
                            <Iconify icon="eva:plus-circle-outline" width={16} />
                            Add treatment context
                        </Box>
                    ) : (
                        <Collapse in={addingMore}>
                            <Box sx={{ maxWidth: 360, mt: 0.5 }}>
                                <Autocomplete
                                    size="small"
                                    options={availableExtras.filter(
                                        (pt) => !(extraTreatmentIds || []).includes(pt.id)
                                    )}
                                    getOptionLabel={(opt) => opt.name || ''}
                                    onChange={(_, val) => {
                                        if (val && onAddExtra) {
                                            onAddExtra(val.id);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search practice treatments..."
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Iconify icon="eva:search-fill" width={16} sx={{ color: '#919EAB' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    fontSize: '13px',
                                                },
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} sx={{ fontSize: '13px', py: '6px !important' }}>
                                            <Iconify icon="mdi:tooth-outline" width={16} sx={{ mr: 1, color: '#919EAB' }} />
                                            {option.name}
                                            {option.treatment_category && (
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    sx={{ ml: 'auto', pl: 1, color: '#919EAB', fontSize: '11px' }}
                                                >
                                                    {option.treatment_category}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    value={null}
                                    blurOnSelect
                                    clearOnBlur
                                    noOptionsText="No matching treatments"
                                    sx={{ mb: 0.5 }}
                                />
                                <Box
                                    onClick={() => setAddingMore(false)}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        color: '#919EAB',
                                        '&:hover': { color: 'text.primary' },
                                    }}
                                >
                                    <Iconify icon="eva:close-fill" width={14} />
                                    Close
                                </Box>
                            </Box>
                        </Collapse>
                    )}
                </Box>
            )}
        </Box>
    );
};

TreatmentSelector.propTypes = {
    treatments: PropTypes.array,
    selectedTreatmentIds: PropTypes.array.isRequired,
    onToggle: PropTypes.func.isRequired,
    practiceTreatments: PropTypes.array,
    extraTreatmentIds: PropTypes.array,
    onAddExtra: PropTypes.func,
    onRemoveExtra: PropTypes.func,
};

TreatmentSelector.defaultProps = {
    treatments: [],
    practiceTreatments: [],
    extraTreatmentIds: [],
    onAddExtra: null,
    onRemoveExtra: null,
};

export default TreatmentSelector;
